import { useState, useEffect } from 'react';
import { api } from '../../lib/api';
import { Mail, MailCheck, MailX, RefreshCw, Download, Loader2, AlertTriangle, CheckCircle, ExternalLink, FileText, Unplug } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GmailSection = ({ onImported }) => {
  const [status, setStatus] = useState(null);
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [importing, setImporting] = useState({});

  useEffect(() => {
    checkStatus();
  }, []);

  const checkStatus = async () => {
    try {
      const res = await api.get('/oauth/gmail/status');
      setStatus(res.data);
      if (res.data.connected) scanEmails();
      else setLoading(false);
    } catch {
      setLoading(false);
    }
  };

  const scanEmails = async () => {
    setScanning(true);
    try {
      const res = await api.get('/oauth/gmail/invoices?max_results=30');
      setEmails(res.data.emails || []);
    } catch { toast.error('Erreur scan Gmail'); }
    setScanning(false);
    setLoading(false);
  };

  const importEmail = async (gmailId) => {
    setImporting(prev => ({ ...prev, [gmailId]: true }));
    try {
      const res = await api.post(`/oauth/gmail/import/${gmailId}`);
      if (res.data.error) {
        if (res.data.duplicate) {
          toast.error('Doublon — cet email a déjà été importé');
        } else {
          toast.error(res.data.error);
        }
      } else {
        const nb = res.data.nb_imported;
        const dups = res.data.nb_duplicates;
        let msg = `${nb} PDF importé${nb > 1 ? 's' : ''}`;
        if (dups > 0) msg += ` · ${dups} doublon${dups > 1 ? 's' : ''} ignoré${dups > 1 ? 's' : ''}`;
        toast.success(msg);
        // Update email status locally
        setEmails(prev => prev.map(e => e.gmail_id === gmailId ? { ...e, already_imported: true } : e));
        if (onImported) onImported();
      }
    } catch { toast.error('Erreur import'); }
    setImporting(prev => ({ ...prev, [gmailId]: false }));
  };

  const disconnect = async () => {
    try {
      await api.post('/oauth/gmail/disconnect');
      setStatus({ connected: false });
      setEmails([]);
      toast.success('Gmail déconnecté');
    } catch { toast.error('Erreur'); }
  };

  const connectUrl = `${API_URL}/api/oauth/gmail/login`;

  const formatEmailDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr);
      return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}`;
    } catch {
      return dateStr.substring(0, 16);
    }
  };

  const extractSender = (from) => {
    if (!from) return '';
    const match = from.match(/^"?([^"<]+)"?\s*</);
    if (match) return match[1].trim();
    return from.split('@')[0].replace(/[._]/g, ' ');
  };

  if (loading) {
    return (
      <div className="lm-card p-4 flex items-center gap-2">
        <Loader2 size={14} className="animate-spin text-lemas-gold" />
        <span className="text-sm text-[rgb(var(--lm-text-muted))]">Connexion Gmail...</span>
      </div>
    );
  }

  // Not connected
  if (!status?.connected) {
    return (
      <div className="lm-card p-4" data-testid="gmail-connect">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Mail size={20} className="text-[rgb(var(--lm-text-muted))]" />
            <div>
              <p className="text-sm font-medium text-[rgb(var(--lm-text))]">Connecter Gmail</p>
              <p className="text-[10px] text-[rgb(var(--lm-text-muted))]">Récupérez vos factures automatiquement depuis votre boîte mail</p>
            </div>
          </div>
          <a href={connectUrl}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-lemas-gold text-[rgb(var(--lm-bg))] text-xs font-medium hover:bg-lemas-gold/90 transition-all"
            data-testid="gmail-connect-btn">
            <Mail size={14} /> Connecter
          </a>
        </div>
      </div>
    );
  }

  // Connected
  return (
    <div className="space-y-3" data-testid="gmail-section">
      {/* Status bar */}
      <div className="lm-card p-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MailCheck size={16} className="text-[rgb(var(--lm-green))]" />
          <span className="text-xs text-[rgb(var(--lm-text-secondary))]">
            <span className="text-[rgb(var(--lm-green))] font-medium">{status.email}</span> connecté
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={scanEmails} disabled={scanning}
            className="flex items-center gap-1 px-2 py-1 rounded text-[10px] bg-[rgb(var(--lm-surface))] text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-text))] transition-colors"
            data-testid="gmail-scan-btn">
            {scanning ? <Loader2 size={10} className="animate-spin" /> : <RefreshCw size={10} />}
            Scanner
          </button>
          <button onClick={disconnect}
            className="flex items-center gap-1 px-2 py-1 rounded text-[10px] text-[rgb(var(--lm-red))] hover:bg-[rgb(var(--lm-red)/0.1)] transition-colors"
            data-testid="gmail-disconnect-btn">
            <Unplug size={10} /> Déconnecter
          </button>
        </div>
      </div>

      {/* Email list */}
      {emails.length > 0 && (
        <div className="lm-card overflow-hidden divide-y divide-[rgb(var(--lm-border))]">
          <div className="px-4 py-2 bg-[rgb(var(--lm-bg)/0.5)]">
            <p className="text-[10px] tracking-[0.15em] uppercase text-[rgb(var(--lm-text-muted))]">
              <Mail size={10} className="inline mr-1" />
              {emails.length} email{emails.length > 1 ? 's' : ''} avec factures
            </p>
          </div>
          {emails.map((email, i) => (
            <div key={email.gmail_id} className="px-4 py-2.5 flex items-center justify-between hover:bg-[rgb(var(--lm-bg)/0.3)] transition-colors"
              data-testid={`gmail-email-${i}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm text-[rgb(var(--lm-text))] truncate">{extractSender(email.from)}</p>
                  {email.nb_pdfs > 0 && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-[rgb(var(--lm-surface))] text-[rgb(var(--lm-text-muted))]">
                      <FileText size={8} className="inline mr-0.5" />{email.nb_pdfs} PDF
                    </span>
                  )}
                </div>
                <p className="text-[10px] text-[rgb(var(--lm-text-muted))] truncate">{email.subject}</p>
                <p className="text-[10px] text-[rgb(var(--lm-text-muted))]">{formatEmailDate(email.date)}</p>
              </div>
              <div className="shrink-0 ml-3">
                {email.already_imported ? (
                  <span className="flex items-center gap-1 text-[10px] text-[rgb(var(--lm-green))] px-2 py-1 rounded bg-[rgb(var(--lm-green)/0.1)]">
                    <CheckCircle size={10} /> Importé
                  </span>
                ) : (
                  <button onClick={() => importEmail(email.gmail_id)} disabled={importing[email.gmail_id]}
                    className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-lemas-gold/10 text-lemas-gold text-[10px] font-medium hover:bg-lemas-gold/20 transition-colors disabled:opacity-50"
                    data-testid={`import-btn-${i}`}>
                    {importing[email.gmail_id] ? (
                      <Loader2 size={10} className="animate-spin" />
                    ) : (
                      <Download size={10} />
                    )}
                    Importer
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {emails.length === 0 && !scanning && (
        <div className="lm-card p-4 text-center">
          <p className="text-sm text-[rgb(var(--lm-text-muted))]">Aucun email avec facture trouvé</p>
        </div>
      )}
    </div>
  );
};

export default GmailSection;
